/**
 * NOXCULT — Audio Engine v4
 * nox-audio.js
 *
 * FIXES:
 *  - playAmbient queues if AudioContext not yet created, fires on first gesture
 *  - null SFX entries safely skipped
 *  - protocol-relative Shopify CDN URLs normalised (// → https://)
 *  - autoplay:false flag respected
 *  - setMasterVolume(0–1) for runtime control
 */
(function (global) {
  'use strict';

  const SESSION_KEY  = 'nox_audio_muted';
  const FADE_IN_DUR  = 3.0;
  const FADE_OUT_DUR = 1.0;

  class NoxAudio {
    constructor(cfg) {
      this.cfg             = cfg || {};
      this._ctx            = null;
      this._masterGain     = null;
      this._sfxBuffers     = {};
      this._muted          = sessionStorage.getItem(SESSION_KEY) === '1';
      this._masterVol      = 1.0;
      this._ambientBuffer  = null;
      this._ambientSource  = null;
      this._ambientGain    = null;
      this._ambientPlaying = false;
      this._ambientQueued  = false;

      this._initOnGesture();
    }

    /* ── context: created on first user gesture ──────────── */
    _initOnGesture() {
      const boot = () => {
        if (this._ctx) return;
        try {
          const Ctx = window.AudioContext || window.webkitAudioContext;
          if (!Ctx) return;
          this._ctx = new Ctx();
          this._masterGain = this._ctx.createGain();
          this._masterGain.gain.value = this._muted ? 0 : this._masterVol;
          this._masterGain.connect(this._ctx.destination);
          this._preloadSFX();
          if (this._ambientQueued && this.cfg.autoplay !== false) {
            this._ambientQueued = false;
            setTimeout(() => this._doPlayAmbient(), 100);
          }
        } catch (e) { console.warn('[NOX Audio]', e); }
      };
      ['click','touchstart','pointerdown','keydown'].forEach(ev =>
        document.addEventListener(ev, boot, { passive: true })
      );
    }

    _preloadSFX() {
      const sfx = this.cfg.sfx;
      if (!sfx) return;
      Object.entries(sfx).forEach(([name, spec]) => {
        if (!spec?.src) return;
        this._loadBuffer(spec.src).then(b => { this._sfxBuffers[name] = b; }).catch(() => {});
      });
    }

    _loadBuffer(url) {
      if (url.startsWith('//')) url = 'https:' + url;
      return fetch(url, { mode: 'cors' })
        .then(r => { if (!r.ok) throw new Error(r.status); return r.arrayBuffer(); })
        .then(ab => this._ctx.decodeAudioData(ab));
    }

    /* ── ambient ─────────────────────────────────────────── */
    playAmbient() {
      if (this.cfg.autoplay === false || this._ambientPlaying) return;
      if (!this._ctx) { this._ambientQueued = true; return; }
      this._doPlayAmbient();
    }

    _doPlayAmbient() {
      if (this._ambientPlaying) return;
      const spec = this.cfg.ambient;
      if (!spec?.src) return;
      const go = (buf) => {
        const gainNode = this._ctx.createGain();
        gainNode.gain.value = 0;
        gainNode.connect(this._masterGain);
        const src = this._ctx.createBufferSource();
        src.buffer = buf; src.loop = true;
        src.connect(gainNode); src.start(0);
        this._ambientGain = gainNode; this._ambientSource = src;
        this._ambientPlaying = true;
        if (!this._muted) this._fadeIn(gainNode, spec.volume ?? 0.18, FADE_IN_DUR);
      };
      if (this._ambientBuffer) { go(this._ambientBuffer); return; }
      this._loadBuffer(spec.src)
        .then(buf => { this._ambientBuffer = buf; go(buf); })
        .catch(e => console.warn('[NOX Audio] ambient:', e));
    }

    stopAmbient() {
      if (!this._ambientPlaying) return;
      const { _ambientGain: g, _ambientSource: s } = this;
      this._ambientPlaying = false; this._ambientGain = null; this._ambientSource = null;
      this._fadeOut(g, FADE_OUT_DUR).then(() => { try { s.stop(); } catch (_) {} });
    }

    /* ── SFX ─────────────────────────────────────────────── */
    playSFX(name) {
      if (this._muted || !this._ctx) return;
      if (this._ctx.state === 'suspended') this._ctx.resume();
      const spec = this.cfg.sfx?.[name];
      const buf  = this._sfxBuffers[name];
      if (!buf || !spec) return;
      const g = this._ctx.createGain();
      g.gain.value = spec.volume ?? 0.5;
      g.connect(this._masterGain);
      const s = this._ctx.createBufferSource();
      s.buffer = buf; s.connect(g); s.start(0);
      s.onended = () => g.disconnect();
    }

    /* ── controls ────────────────────────────────────────── */
    setMasterVolume(v) {
      this._masterVol = Math.max(0, Math.min(1, v));
      if (this._masterGain && !this._muted && this._ctx)
        this._masterGain.gain.setTargetAtTime(this._masterVol, this._ctx.currentTime, 0.1);
    }

    toggle() {
      this._muted = !this._muted;
      sessionStorage.setItem(SESSION_KEY, this._muted ? '1' : '0');
      if (this._masterGain && this._ctx) {
        if (this._muted) {
          this._fadeOut(this._masterGain, 0.35);
        } else {
          this._masterGain.gain.cancelScheduledValues(this._ctx.currentTime);
          this._masterGain.gain.setTargetAtTime(this._masterVol, this._ctx.currentTime, 0.25);
          if (!this._ambientPlaying) this._doPlayAmbient();
        }
      }
      return this._muted;
    }

    isMuted() { return this._muted; }

    _fadeIn(g, target, dur) {
      const t = this._ctx.currentTime;
      g.gain.cancelScheduledValues(t);
      g.gain.setValueAtTime(0, t);
      g.gain.linearRampToValueAtTime(target, t + dur);
    }

    _fadeOut(g, dur) {
      return new Promise(res => {
        if (!this._ctx) { res(); return; }
        const t = this._ctx.currentTime;
        g.gain.cancelScheduledValues(t);
        g.gain.setValueAtTime(g.gain.value, t);
        g.gain.linearRampToValueAtTime(0, t + dur);
        setTimeout(res, dur * 1000 + 60);
      });
    }
  }

  global.NoxAudio = NoxAudio;
})(window);
